import { fireEvent, render, screen, within } from "@testing-library/react";
import "@testing-library/jest-dom";
import { buildGeometryMap, CanvasRenderer } from "./CanvasRenderer";
import type { RenderableElement } from "./CanvasRenderer";
import type { CanvasGridConfig } from "../../types";
import { CanvasRuntimeProvider } from "./CanvasRuntimeContext";

const gridConfig: CanvasGridConfig = {
  columns: 12,
  rowHeight: 60,
  gap: 8,
  minRows: 4,
};

const viewGridElement: RenderableElement = {
  id: "grid-1",
  type: "view_grid",
  props: {
    title: "Company Browse",
    data_source_alias: "main",
  },
  gridColumn: 1,
  gridRow: 1,
  columnSpan: 12,
  rowSpan: 4,
};

describe("CanvasRenderer view_grid elements", () => {
  it("delegates to the authenticated runtime renderer when provided", () => {
    const renderViewGrid = jest.fn(() => <div>runtime grid</div>);

    render(
      <CanvasRenderer
        elements={[viewGridElement]}
        config={gridConfig}
        mode="runtime"
        renderViewGrid={renderViewGrid}
      />,
    );

    expect(screen.getByText("runtime grid")).toBeTruthy();
    expect(renderViewGrid).toHaveBeenCalledWith(viewGridElement);
  });

  it("renders a safe placeholder when no authenticated grid renderer is provided", () => {
    render(
      <CanvasRenderer
        elements={[viewGridElement]}
        config={gridConfig}
        mode="runtime"
      />,
    );

    expect(screen.getByText("Company Browse")).toBeTruthy();
    expect(
      screen.getByText("View grids require an authenticated canvas."),
    ).toBeTruthy();
  });
});

describe("CanvasRenderer read-only field controls", () => {
  it("marks the native textarea read-only while keeping its value selectable", () => {
    const textarea: RenderableElement = {
      id: "position-description",
      type: "field",
      variant: "textarea",
      props: { label: "Position description", readonly: true, rows: 4 },
      gridColumn: 1,
      gridRow: 1,
      columnSpan: 12,
      rowSpan: 3,
    };

    render(
      <CanvasRenderer
        elements={[textarea]}
        config={gridConfig}
        mode="runtime"
        fieldState={{
          "position-description": { value: "Line one\nLine two" },
        }}
      />,
    );

    const nativeTextarea = screen.getByRole("textbox", {
      name: "Position description",
    });
    expect(nativeTextarea).toHaveAttribute("readonly");
    expect(nativeTextarea).toHaveAttribute("aria-readonly", "true");
    expect(nativeTextarea).not.toBeDisabled();
  });
});

describe("CanvasRenderer selection detail", () => {
  const panel: RenderableElement = {
    id: "panel-1",
    type: "container",
    props: {
      containerType: "SECTION",
      selection_binding: {
        source_grid_element_id: "grid-1",
        data_source_alias: "main",
        mode: "read",
        empty_state_text: "Select a person",
      },
    },
    gridColumn: 1,
    gridRow: 1,
    columnSpan: 12,
    rowSpan: 4,
  };
  const value: RenderableElement = {
    id: "value-1",
    type: "static",
    props: { label: "Employee" },
    data_binding: { data_source_id: "main", field_path: "name" },
    parentElementId: "panel-1",
    gridColumn: 1,
    gridRow: 1,
    columnSpan: 12,
    rowSpan: 1,
  };

  it("shows the empty state until a selected, permission-checked record is ready", () => {
    const { rerender } = render(
      <CanvasRuntimeProvider
        value={{
          workspaceId: "w",
          canvasId: "c",
          rowId: null,
          rowData: null,
          selectionDetail: { rowId: null, loading: false, error: null },
        }}
      >
        <CanvasRenderer
          elements={[panel, value]}
          config={gridConfig}
          mode="runtime"
          fieldState={{ name: { value: "Ada" } }}
        />
      </CanvasRuntimeProvider>,
    );
    expect(screen.getByText("Select a person")).toBeInTheDocument();
    expect(screen.queryByText("Ada")).not.toBeInTheDocument();

    rerender(
      <CanvasRuntimeProvider
        value={{
          workspaceId: "w",
          canvasId: "c",
          rowId: "r1",
          rowData: { name: "Ada" },
          selectionDetail: { rowId: "r1", loading: false, error: null },
        }}
      >
        <CanvasRenderer
          elements={[panel, value]}
          config={gridConfig}
          mode="runtime"
          fieldState={{ name: { value: "Ada" } }}
        />
      </CanvasRuntimeProvider>,
    );
    expect(screen.getByText("Ada")).toBeInTheDocument();
  });
});

describe("CanvasRenderer timeline elements", () => {
  const timeline = {
    ...viewGridElement,
    id: "timeline-1",
    type: "timeline",
    props: {
      title: "History",
      data_source_alias: "events",
      widget_type: "TIMELINE",
      timeline_config: { label_field: "stage" },
    },
  };

  it("delegates the runtime widget to the bound timeline renderer", () => {
    const renderTimeline = jest.fn(() => <div>bound stages</div>);
    render(
      <CanvasRenderer
        elements={[timeline]}
        config={gridConfig}
        mode="runtime"
        renderTimeline={renderTimeline}
      />,
    );
    expect(screen.getByText("bound stages")).toBeTruthy();
    expect(renderTimeline).toHaveBeenCalledWith(timeline);
  });

  it("shows a useful builder preview", () => {
    render(
      <CanvasRenderer elements={[timeline]} config={gridConfig} mode="edit" />,
    );
    expect(screen.getByText("History")).toBeTruthy();
    expect(screen.getByText("Timeline from events")).toBeTruthy();
  });
});

describe("buildGeometryMap compactDesktop option (not used by the desktop runtime renderer)", () => {
  const elements: RenderableElement[] = [
    {
      id: "panel",
      type: "container",
      props: {},
      gridColumn: 1,
      gridRow: 2,
      columnSpan: 12,
      rowSpan: 2,
    },
    {
      id: "child",
      type: "text",
      props: { label: "Visible child" },
      parentElementId: "panel",
      gridColumn: 7,
      gridRow: 3,
      columnSpan: 6,
      rowSpan: 1,
    },
    {
      id: "next",
      type: "text",
      props: { label: "Next" },
      gridColumn: 1,
      gridRow: 5,
      columnSpan: 6,
      rowSpan: 1,
    },
  ];

  it.each(["desktop", "tablet", "phone"] as const)(
    "closes root and nested gaps at %s when compactDesktop is passed explicitly",
    (breakpoint) => {
      const columns =
        breakpoint === "phone" ? 1 : breakpoint === "tablet" ? 8 : 12;
      const geometry = buildGeometryMap(
        elements,
        breakpoint,
        columns,
        12,
        true,
        true,
      );
      expect(geometry?.get("panel")?.row).toBe(1);
      expect(geometry?.get("child")?.row).toBe(1);
      expect(geometry?.get("next")?.row).toBe(3);
    },
  );

  it("leaves authored builder positions unchanged", () => {
    const geometry = buildGeometryMap(elements, "desktop", 12, 12, true);
    expect(geometry?.get("panel")?.row).toBe(2);
    expect(geometry?.get("child")?.row).toBe(3);
  });
});

function staticElement(props: Record<string, unknown>): RenderableElement {
  return {
    id: "static-1",
    type: "static",
    props,
    gridColumn: 1,
    gridRow: 1,
    columnSpan: 4,
    rowSpan: 2,
  };
}

function renderStatic(props: Record<string, unknown>) {
  return render(
    <CanvasRenderer
      elements={[staticElement(props)]}
      config={gridConfig}
      mode="runtime"
    />,
  );
}

describe("CanvasRenderer static IMAGE and DIVIDER elements", () => {
  it.each([
    ["absolute https", "https://example.com/logo.png"],
    ["absolute http", "http://example.com/logo.png"],
    ["root relative", "/assets/logo.png"],
    ["relative", "assets/logo.png"],
  ])("renders an image for a %s url with alt text", (_name, url) => {
    renderStatic({
      textVariant: "image",
      imageUrl: url,
      imageAlt: "Company logo",
    });

    const image = screen.getByRole("img", { name: "Company logo" });
    expect(image).toHaveAttribute("src", url);
  });

  it("reads image_url when imageUrl is absent and applies imageFit", () => {
    renderStatic({
      textVariant: "image",
      image_url: "https://example.com/a.png",
      imageAlt: "A",
      imageFit: "cover",
    });

    const image = screen.getByRole("img", { name: "A" });
    expect(image).toHaveAttribute("src", "https://example.com/a.png");
    expect(image).toHaveStyle({ objectFit: "cover" });
  });

  it.each([
    ["java", "script:alert(1)"].join(""),
    ["JaVa", "ScRiPt:alert(1)"].join(""),
    "data:text/html,<script>alert(1)</script>",
    "//evil.example.com/x.png",
    "ftp://example.com/x.png",
  ])("does not render an image for the unsafe url %s", (url) => {
    renderStatic({
      textVariant: "image",
      imageUrl: url,
      imageAlt: "Bad",
    });

    expect(screen.queryByRole("img")).toBeNull();
    expect(screen.getByText("Image unavailable")).toBeInTheDocument();
  });

  it("shows a placeholder when an image has no url", () => {
    renderStatic({ textVariant: "image" });

    expect(screen.queryByRole("img")).toBeNull();
    expect(screen.getByText("Image unavailable")).toBeInTheDocument();
  });

  it("renders a divider", () => {
    renderStatic({ textVariant: "divider" });

    expect(screen.getByRole("separator")).toBeInTheDocument();
  });

  it("still renders unknown variants as plain text", () => {
    renderStatic({ textVariant: "text", label: "Plain" });

    expect(screen.getByText("Plain")).toBeInTheDocument();
  });
});

describe("CanvasRenderer static IMAGE and DIVIDER snake_case keys", () => {
  it("reads image_alt and image_fit as stored by the API", () => {
    renderStatic({
      textVariant: "image",
      imageUrl: "https://example.com/a.png",
      image_alt: "Snake alt",
      image_fit: "cover",
    });

    const image = screen.getByRole("img", { name: "Snake alt" });
    expect(image).toHaveStyle({ objectFit: "cover" });
  });

  it("reads divider_style", () => {
    renderStatic({
      textVariant: "divider",
      divider_style: "dashed",
    });

    expect(screen.getByRole("separator")).toHaveStyle({
      borderStyle: "dashed",
    });
  });

  it.each([42, { href: "x" }, ["a.png"], true])(
    "shows the placeholder instead of throwing for a non-string imageUrl %p",
    (value) => {
      renderStatic({
        textVariant: "image",
        imageUrl: value,
      });

      expect(screen.queryByRole("img")).toBeNull();
      expect(screen.getByText("Image unavailable")).toBeInTheDocument();
    },
  );
});

describe("CanvasRenderer tab controls", () => {
  const tabsElement: RenderableElement = {
    id: "tabs-1",
    type: "container",
    props: {
      containerType: "TABS",
      panels: [
        { id: "panel-a", label: "Alpha" },
        { id: "panel-b", label: "Beta" },
      ],
    } as unknown as RenderableElement["props"],
    gridColumn: 1,
    gridRow: 1,
    columnSpan: 12,
    rowSpan: 4,
  };

  it("reports the shown panel of a tab control, first and after a change", () => {
    const onActivePanelChange = jest.fn();
    render(
      <CanvasRenderer
        elements={[tabsElement]}
        config={gridConfig}
        mode="runtime"
        onActivePanelChange={onActivePanelChange}
      />,
    );

    expect(onActivePanelChange).toHaveBeenLastCalledWith("tabs-1", "panel-a");

    fireEvent.click(screen.getByRole("tab", { name: "Beta" }));
    expect(onActivePanelChange).toHaveBeenLastCalledWith("tabs-1", "panel-b");
  });
});

describe("CanvasRenderer desktop runtime keeps authored rows", () => {
  const sideCard: RenderableElement = {
    id: "side-card",
    type: "static",
    props: { textVariant: "text", label: "My role" } as never,
    gridColumn: 9,
    gridRow: 1,
    columnSpan: 4,
    rowSpan: 11,
  };
  const title: RenderableElement = {
    id: "title",
    type: "static",
    props: { textVariant: "text", label: "Personal details" } as never,
    gridColumn: 1,
    gridRow: 1,
    columnSpan: 8,
    rowSpan: 1,
  };
  const field: RenderableElement = {
    id: "field",
    type: "static",
    props: { textVariant: "text", label: "Legal First Name" } as never,
    gridColumn: 1,
    gridRow: 2,
    columnSpan: 4,
    rowSpan: 1,
  };

  function css(): string {
    return Array.from(document.styleSheets)
      .flatMap((sheet) =>
        Array.from(sheet.cssRules).map((rule) => rule.cssText),
      )
      .join("\n");
  }

  it("places a field authored beside an 11-row side card at its own row, not below the card", () => {
    render(
      <CanvasRenderer
        elements={[title, sideCard, field]}
        config={gridConfig}
        mode="runtime"
        responsive
        forcedBreakpoint="desktop"
      />,
    );
    const rules = css();
    // The field (authored at row 2, 4 columns wide) stays at row 2 ...
    expect(rules).toMatch(/grid-row:\s*2\s*\/\s*span 1/);
    // ... and is not shelf-packed under the side card (which ends at row 11).
    expect(rules).not.toMatch(/grid-row:\s*12\s*\/\s*span 1/);
  });

  it("still packs shelves at the tablet breakpoint", () => {
    render(
      <CanvasRenderer
        elements={[title, sideCard, field]}
        config={gridConfig}
        mode="runtime"
        responsive
        forcedBreakpoint="tablet"
      />,
    );
    expect(css()).toMatch(/grid-row:\s*12\s*\/\s*span 1/);
  });
});

describe("CanvasRenderer status pills and card tint", () => {
  it("renders a bound static value as a coloured pill when statusColors is set", () => {
    const element: RenderableElement = {
      id: "status",
      type: "static",
      props: {
        textVariant: "text",
        statusColors: { Active: "success", Vacant: "warning" },
      } as never,
      data_binding: { data_source_id: "main", field_path: "status" },
      gridColumn: 1,
      gridRow: 1,
      columnSpan: 4,
      rowSpan: 1,
    };
    render(
      <CanvasRenderer
        elements={[element]}
        config={gridConfig}
        mode="runtime"
        fieldState={{ status: { value: "Vacant" } } as never}
      />,
    );
    expect(screen.getByTestId("status-chip")).toHaveAttribute(
      "data-status-color",
      "warning",
    );
  });

  it("still renders a bound static value as plain text without statusColors", () => {
    const element: RenderableElement = {
      id: "plain",
      type: "static",
      props: { textVariant: "text" } as never,
      data_binding: { data_source_id: "main", field_path: "status" },
      gridColumn: 1,
      gridRow: 1,
      columnSpan: 4,
      rowSpan: 1,
    };
    render(
      <CanvasRenderer
        elements={[element]}
        config={gridConfig}
        mode="runtime"
        fieldState={{ status: { value: "Vacant" } } as never}
      />,
    );
    expect(screen.getByText("Vacant")).toBeInTheDocument();
    expect(screen.queryByTestId("status-chip")).toBeNull();
  });

  it("renders a bound static value as initials plus the name when avatar is set", () => {
    const element: RenderableElement = {
      id: "person",
      type: "static",
      props: { textVariant: "text", avatar: true } as never,
      data_binding: { data_source_id: "main", field_path: "reviewer" },
      gridColumn: 1,
      gridRow: 1,
      columnSpan: 4,
      rowSpan: 1,
    };
    render(
      <CanvasRenderer
        elements={[element]}
        config={gridConfig}
        mode="runtime"
        fieldState={{ reviewer: { value: "Ada Lovelace" } } as never}
      />,
    );
    expect(screen.getByTestId("initials-avatar")).toHaveTextContent("AL");
    expect(screen.getByText("Ada Lovelace")).toBeInTheDocument();
  });

  it("does not add an avatar to a bound static value unless avatar is set", () => {
    const element: RenderableElement = {
      id: "person-plain",
      type: "static",
      props: { textVariant: "text" } as never,
      data_binding: { data_source_id: "main", field_path: "reviewer" },
      gridColumn: 1,
      gridRow: 1,
      columnSpan: 4,
      rowSpan: 1,
    };
    render(
      <CanvasRenderer
        elements={[element]}
        config={gridConfig}
        mode="runtime"
        fieldState={{ reviewer: { value: "Ada Lovelace" } } as never}
      />,
    );
    expect(screen.getByText("Ada Lovelace")).toBeInTheDocument();
    expect(screen.queryByTestId("initials-avatar")).toBeNull();
  });

  it("tints and accents a card from style_overrides, ignoring anything outside the whitelist", () => {
    const card: RenderableElement = {
      id: "card",
      type: "container",
      props: { containerType: "CARD", title: "My role" } as never,
      styleOverrides: {
        backgroundColor: "token:primary.50",
        accentColor: "#009ccf",
        position: "fixed",
      },
      gridColumn: 1,
      gridRow: 1,
      columnSpan: 4,
      rowSpan: 4,
    };
    render(
      <CanvasRenderer elements={[card]} config={gridConfig} mode="runtime" />,
    );
    const rules = Array.from(document.styleSheets)
      .flatMap((sheet) =>
        Array.from(sheet.cssRules).map((rule) => rule.cssText),
      )
      .join("\n");
    expect(rules).toMatch(
      /background-color:\s*var\(--joy-palette-primary-50\)/,
    );
    expect(rules).toMatch(/border-left:\s*4px solid #009ccf/);
    expect(rules).not.toMatch(/position:\s*fixed/);
  });
});

describe("CanvasRenderer tab icons and default panel", () => {
  function tabs(props: Record<string, unknown>): RenderableElement {
    return {
      id: "tabs-icons",
      type: "container",
      props: {
        containerType: "TABS",
        panels: [
          { id: "one", label: "Personal details", icon: "Person" },
          { id: "two", label: "Documents" },
          { id: "three", label: "Equipment", icon: "NotAnIcon" },
        ],
        ...props,
      } as unknown as RenderableElement["props"],
      gridColumn: 1,
      gridRow: 1,
      columnSpan: 12,
      rowSpan: 4,
    };
  }

  it("renders a panel's icon in the tab strip when it names a MUI icon", () => {
    render(
      <CanvasRenderer
        elements={[tabs({})]}
        config={gridConfig}
        mode="runtime"
      />,
    );
    const withIcon = screen.getByRole("tab", { name: /Personal details/ });
    expect(within(withIcon).getByTestId("PersonIcon")).toBeInTheDocument();
    // No icon key, or an unknown icon name, leaves the label alone.
    expect(
      within(screen.getByRole("tab", { name: "Documents" })).queryByTestId(
        /Icon$/,
      ),
    ).toBeNull();
    expect(
      within(screen.getByRole("tab", { name: "Equipment" })).queryByTestId(
        /Icon$/,
      ),
    ).toBeNull();
  });

  it("opens on defaultPanelId instead of the first panel", () => {
    const onActivePanelChange = jest.fn();
    render(
      <CanvasRenderer
        elements={[tabs({ defaultPanelId: "two" })]}
        config={gridConfig}
        mode="runtime"
        onActivePanelChange={onActivePanelChange}
      />,
    );
    expect(screen.getByRole("tab", { name: "Documents" })).toHaveAttribute(
      "aria-selected",
      "true",
    );
    expect(onActivePanelChange).toHaveBeenLastCalledWith("tabs-icons", "two");
  });

  it("falls back to the first panel when defaultPanelId names no panel", () => {
    render(
      <CanvasRenderer
        elements={[tabs({ defaultPanelId: "missing" })]}
        config={gridConfig}
        mode="runtime"
      />,
    );
    expect(
      screen.getByRole("tab", { name: /Personal details/ }),
    ).toHaveAttribute("aria-selected", "true");
  });
});

describe("CanvasRenderer radio groups and read-only text fields", () => {
  const options = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
  ];
  function field(
    variant: string,
    props: Record<string, unknown>,
  ): RenderableElement {
    return {
      id: `field-${variant}`,
      type: "field",
      variant,
      props: { label: "Right to work", options, ...props } as never,
      data_binding: { data_source_id: "main", field_path: "right_to_work" },
      gridColumn: 1,
      gridRow: 1,
      columnSpan: 6,
      rowSpan: 2,
    };
  }
  function renderField(
    element: RenderableElement,
    state: { value?: unknown; onChange?: (value: unknown) => void },
  ) {
    return render(
      <CanvasRenderer
        elements={[element]}
        config={gridConfig}
        mode="runtime"
        fieldState={{ right_to_work: state } as never}
      />,
    );
  }

  it("renders a radio group over options with the stored value selected", () => {
    renderField(field("radio", {}), { value: "no" });
    expect(screen.getByRole("radiogroup")).toBeInTheDocument();
    expect(screen.getByRole("radio", { name: "Yes" })).not.toBeChecked();
    expect(screen.getByRole("radio", { name: "No" })).toBeChecked();
  });

  it("reports the chosen option's value", () => {
    const onChange = jest.fn();
    renderField(field("radio", {}), { value: "no", onChange });
    fireEvent.click(screen.getByRole("radio", { name: "Yes" }));
    expect(onChange).toHaveBeenCalledWith("yes");
  });

  it("disables the radios when the field is read-only", () => {
    renderField(field("radio", { readonly: true }), { value: "yes" });
    expect(screen.getByRole("radio", { name: "Yes" })).toBeDisabled();
  });

  it("shows a select field as label and text when displayMode is text", () => {
    renderField(field("select", { displayMode: "text" }), { value: "no" });
    expect(screen.getByText("Right to work")).toBeInTheDocument();
    expect(screen.getByText("No")).toBeInTheDocument();
    expect(screen.queryByRole("combobox")).toBeNull();
    expect(screen.queryByRole("textbox")).toBeNull();
  });

  it("renders string options stored on agent-created select fields as labels", () => {
    renderField(
      field("select", {
        label: "Stage",
        options: ["Open", "Won", "Lost"],
      }),
      { value: undefined },
    );

    const listbox = screen.getByRole("listbox", { hidden: true });
    expect(
      within(listbox).getByRole("option", { name: "Open", hidden: true }),
    ).toBeInTheDocument();
    expect(
      within(listbox).getByRole("option", { name: "Won", hidden: true }),
    ).toBeInTheDocument();
    expect(
      within(listbox).getByRole("option", { name: "Lost", hidden: true }),
    ).toBeInTheDocument();
  });

  it("keeps labeled object options and displays string options in text mode", () => {
    const { rerender } = renderField(
      field("select", {
        label: "Stage",
        options: [{ value: "won", label: "Won deal" }],
      }),
      { value: undefined },
    );
    expect(
      within(screen.getByRole("listbox", { hidden: true })).getByRole(
        "option",
        {
          name: "Won deal",
          hidden: true,
        },
      ),
    ).toBeInTheDocument();

    rerender(
      <CanvasRenderer
        elements={[
          field("select", {
            label: "Stage",
            displayMode: "text",
            options: ["Open", "Won", "Lost"],
          }),
        ]}
        config={gridConfig}
        mode="runtime"
        fieldState={{ right_to_work: { value: "Won" } } as never}
      />,
    );
    expect(screen.getByText("Won")).toBeInTheDocument();
  });

  it("shows text, boolean and empty values in text mode", () => {
    const { unmount } = renderField(
      field("text-input", { displayMode: "text" }),
      {
        value: "Alex",
      },
    );
    expect(screen.getByText("Alex")).toBeInTheDocument();
    unmount();
    const view = renderField(field("boolean", { displayMode: "text" }), {
      value: true,
    });
    expect(screen.getByText("Yes")).toBeInTheDocument();
    view.unmount();
    renderField(field("text-input", { displayMode: "text" }), { value: "" });
    expect(screen.getByText("—")).toBeInTheDocument();
  });
});

describe("CanvasRenderer styled non-card elements", () => {
  it("wraps a styled element so it still fills its grid cell", () => {
    const widget: RenderableElement = {
      id: "styled-static",
      type: "static",
      props: { textVariant: "text", label: "Note" } as never,
      styleOverrides: {
        backgroundColor: "token:primary.50",
        accentColor: "#123abc",
      },
      gridColumn: 1,
      gridRow: 1,
      columnSpan: 6,
      rowSpan: 3,
    };
    render(
      <CanvasRenderer elements={[widget]} config={gridConfig} mode="runtime" />,
    );
    const wrapperRule = Array.from(document.styleSheets)
      .flatMap((sheet) =>
        Array.from(sheet.cssRules).map((rule) => rule.cssText),
      )
      .find((text) => /border-left:\s*4px solid #123abc/.test(text));
    expect(wrapperRule).toMatch(/flex-grow:\s*1/);
    expect(wrapperRule).toMatch(/min-height:\s*0/);
  });
});

describe("CanvasRenderer auto_height", () => {
  const text = (
    id: string,
    row: number,
    rowSpan: number,
    props: Record<string, unknown> = {},
  ): RenderableElement => ({
    id,
    type: "text",
    props: { label: id, ...props },
    gridColumn: 1,
    gridRow: row,
    columnSpan: 12,
    rowSpan,
  });
  const item = (id: string) => screen.getByTestId(`canvas-element-${id}`);
  const renderRoot = (elements: RenderableElement[]) =>
    render(
      <CanvasRenderer elements={elements} config={gridConfig} mode="runtime" />,
    );

  it("leaves a canvas without auto_height exactly as before", () => {
    renderRoot([text("a", 1, 3), text("b", 5, 2)]);
    expect(screen.getByTestId("canvas-root-grid")).toHaveStyle({
      gridAutoRows: "60px",
    });
    expect(item("a")).toHaveStyle({ justifyContent: "center" });
    expect(item("a").style.alignSelf).toBe("");
    expect(item("a").style.minHeight).toBe("");
  });

  it("sizes an auto_height element to its content and keeps the others at their authored height", () => {
    renderRoot([text("a", 1, 3, { auto_height: true }), text("b", 5, 2)]);
    const grid = screen.getByTestId("canvas-root-grid");
    expect(grid).toHaveStyle({ gridAutoRows: "minmax(0, auto)" });
    expect(grid.style.minHeight).toBe("");
    // The auto element starts at the top of its rows and takes only what it needs.
    expect(item("a")).toHaveStyle({
      alignSelf: "start",
      justifyContent: "flex-start",
    });
    expect(item("a").style.minHeight).toBe("");
    // A neighbour keeps its rows: 2 x 60 px plus one 8 px gap.
    expect(item("b")).toHaveStyle({ minHeight: "128px" });
  });

  it("only an auto_height of true counts", () => {
    renderRoot([
      text("a", 1, 3, { auto_height: "yes" }),
      text("b", 5, 2, { auto_height: false }),
    ]);
    expect(screen.getByTestId("canvas-root-grid")).toHaveStyle({
      gridAutoRows: "60px",
    });
    expect(item("a").style.alignSelf).toBe("");
  });
});

test("RELATED_RECORDS delegates to the authenticated runtime instead of an unknown element", () => {
  const element = {
    ...viewGridElement,
    type: "related_records",
  } as RenderableElement;
  const renderer = jest.fn(() => <div>Scoped related records</div>);
  render(
    <CanvasRenderer
      elements={[element]}
      config={gridConfig}
      mode="runtime"
      renderDataWidget={renderer}
    />,
  );
  expect(screen.getByText("Scoped related records")).toBeInTheDocument();
  expect(renderer).toHaveBeenCalledWith(
    expect.objectContaining({ id: "grid-1", type: "related_records" }),
  );
});

describe("saved element visibility", () => {
  const field = {
    ...viewGridElement,
    type: "field",
    props: { label: "Hidden name", displayMode: "text" },
    isVisible: false,
  } as RenderableElement;
  it("hides a saved hidden FIELD at runtime but retains it for editors", () => {
    const { rerender } = render(
      <CanvasRenderer elements={[field]} config={gridConfig} mode="runtime" />,
    );
    expect(screen.queryByText("Hidden name")).not.toBeInTheDocument();
    rerender(
      <CanvasRenderer elements={[field]} config={gridConfig} mode="edit" />,
    );
    expect(screen.getByText("Hidden name")).toBeInTheDocument();
  });
  it("hides saved hidden nested fields in exact preview while keeping them in the editor", () => {
    const container: RenderableElement = {
      ...viewGridElement,
      id: "preview-parent",
      type: "container",
      props: { containerType: "SECTION" } as never,
    };
    const nested = {
      ...field,
      id: "preview-hidden-field",
      parentElementId: container.id,
    };
    const { rerender } = render(
      <CanvasRenderer
        elements={[container, nested]}
        config={gridConfig}
        mode="edit"
        respectSavedVisibility
      />,
    );
    expect(screen.queryByText("Hidden name")).not.toBeInTheDocument();

    rerender(
      <CanvasRenderer
        elements={[container, nested]}
        config={gridConfig}
        mode="edit"
      />,
    );
    expect(screen.getByText("Hidden name")).toBeInTheDocument();
  });
  it("hides nested saved fields and conditional children at runtime, but shows both to editors", () => {
    const container: RenderableElement = {
      ...viewGridElement,
      id: "parent",
      type: "container",
      props: { containerType: "SECTION" } as never,
    };
    const nested = { ...field, parentElementId: "parent" };
    const conditional: RenderableElement = {
      ...nested,
      id: "conditional",
      isVisible: true,
      props: { label: "Conditional name", displayMode: "text" } as never,
      visibilityRules: {
        logic: "and",
        conditions: [{ field: "status", operator: "eq", value: "approved" }],
      },
    };
    const { rerender } = render(
      <CanvasRenderer
        elements={[container, nested, conditional]}
        config={gridConfig}
        mode="runtime"
        fieldState={{ status: { value: "pending" } }}
      />,
    );
    expect(screen.queryByText("Hidden name")).not.toBeInTheDocument();
    expect(screen.queryByText("Conditional name")).not.toBeInTheDocument();
    rerender(
      <CanvasRenderer
        elements={[container, nested, conditional]}
        config={gridConfig}
        mode="runtime"
        fieldState={{ status: { value: "approved" } }}
      />,
    );
    expect(screen.queryByText("Hidden name")).not.toBeInTheDocument();
    expect(screen.getByText("Conditional name")).toBeInTheDocument();
    rerender(
      <CanvasRenderer
        elements={[container, nested, conditional]}
        config={gridConfig}
        mode="edit"
      />,
    );
    expect(screen.getByText("Hidden name")).toBeInTheDocument();
    expect(screen.getByText("Conditional name")).toBeInTheDocument();
  });
  it("keeps omitted visibility visible and continues applying conditional rules", () => {
    const visible = { ...field, isVisible: undefined } as RenderableElement;
    const { rerender } = render(
      <CanvasRenderer
        elements={[visible]}
        config={gridConfig}
        mode="runtime"
      />,
    );
    expect(screen.getByText("Hidden name")).toBeInTheDocument();
    const conditional = {
      ...visible,
      visibilityRules: {
        logic: "and",
        conditions: [{ field: "status", operator: "eq", value: "approved" }],
      },
    } as RenderableElement;
    rerender(
      <CanvasRenderer
        elements={[conditional]}
        config={gridConfig}
        mode="runtime"
        fieldState={{ status: { value: "pending" } }}
      />,
    );
    expect(screen.queryByText("Hidden name")).not.toBeInTheDocument();
    rerender(
      <CanvasRenderer
        elements={[conditional]}
        config={gridConfig}
        mode="runtime"
        fieldState={{ status: { value: "approved" } }}
      />,
    );
    expect(screen.getByText("Hidden name")).toBeInTheDocument();
  });
});

test.each(["related_records", "hierarchy"])(
  "%s delegates to the authenticated runtime instead of an unknown element",
  (type) => {
    const element = {
      ...viewGridElement,
      type,
    } as RenderableElement;
    const renderer = jest.fn(() => <div>Scoped related records</div>);
    render(
      <CanvasRenderer
        elements={[element]}
        config={gridConfig}
        mode="runtime"
        renderDataWidget={renderer}
      />,
    );
    expect(screen.getByText("Scoped related records")).toBeInTheDocument();
    expect(renderer).toHaveBeenCalledWith(
      expect.objectContaining({ id: "grid-1", type }),
    );
  },
);
